import { useState } from "react";
import type { DailyPoint } from "@/api";
import { fmtMs } from "@/utils/format";

type Tab = "visitors" | "submits" | "duration";

const TABS: { key: Tab; label: string }[] = [
  { key: "visitors", label: "Visitors" },
  { key: "submits", label: "Submits" },
  { key: "duration", label: "Time to complete" },
];

const W = 640;
const H = 160;
const PAD = 8;

function value(p: DailyPoint, tab: Tab): number | null {
  if (tab === "visitors") return p.visitors;
  if (tab === "submits") return p.submits;
  return p.avg_duration_ms ?? null; // zero-traffic days render as gaps
}

/** Polyline segments, split where the metric has no value (gap days). */
function segments(daily: DailyPoint[], tab: Tab, max: number): string[] {
  const xStep = daily.length > 1 ? (W - PAD * 2) / (daily.length - 1) : 0;
  const segs: string[][] = [[]];
  daily.forEach((p, i) => {
    const v = value(p, tab);
    if (v == null) {
      if (segs[segs.length - 1].length > 0) segs.push([]);
      return;
    }
    const x = PAD + i * xStep;
    const y = H - PAD - (max > 0 ? (v / max) * (H - PAD * 2) : 0);
    segs[segs.length - 1].push(`${x.toFixed(1)},${y.toFixed(1)}`);
  });
  return segs.filter((s) => s.length > 0).map((s) => s.join(" "));
}

export function TimeSeriesChart({ daily }: { daily: DailyPoint[] }) {
  const [tab, setTab] = useState<Tab>("visitors");
  if (daily.length === 0) return null;

  const values = daily.map((p) => value(p, tab)).filter((v): v is number => v != null);
  const max = values.length > 0 ? Math.max(...values, 1) : 1;
  const peak = tab === "duration" ? fmtMs(max) : String(max);
  const first = daily[0].day;
  const last = daily[daily.length - 1].day;

  return (
    <div className="card p-5">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="label">Daily trend</div>
        <div role="tablist" className="flex gap-1">
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              type="button"
              aria-selected={tab === t.key}
              className={`rounded-full px-3 py-1 text-xs font-semibold ${
                tab === t.key ? "bg-ink-3 text-text" : "text-dim hover:text-text"
              }`}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-40 w-full"
        role="img"
        aria-label={`Daily ${TABS.find((t) => t.key === tab)?.label ?? tab}`}
      >
        <line x1={PAD} y1={H - PAD} x2={W - PAD} y2={H - PAD} stroke="currentColor" opacity={0.15} />
        {segments(daily, tab, max).map((points, i) =>
          points.includes(" ") ? (
            <polyline
              key={i}
              points={points}
              fill="none"
              stroke="var(--color-lime, #d3ff4e)"
              strokeWidth={2}
              strokeLinejoin="round"
            />
          ) : (
            // A lone day between gaps has no line to ride — mark the point.
            <circle
              key={i}
              cx={Number(points.split(",")[0])}
              cy={Number(points.split(",")[1])}
              r={2.5}
              fill="var(--color-lime, #d3ff4e)"
            />
          ),
        )}
      </svg>
      <div className="mt-2 flex items-center justify-between text-xs text-faint">
        <span>{first}</span>
        <span className="text-dim">peak {peak}</span>
        <span>{last}</span>
      </div>
    </div>
  );
}
